import { HrefLink as Link } from "@/components/ui/href-link";
import { RequirePermission } from "@/hooks/shared/CheckAuth";
import { createFileRoute } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";

import { EmptyState } from "@/components/dashboard/data-table/empty-state";
import { FilterBar } from "@/components/dashboard/data-table/filter-bar";
import { Pagination } from "@/components/dashboard/data-table/pagination";
import { SortHeader } from "@/components/dashboard/data-table/sort-header";
import {
  TBody,
  TD,
  TEmpty,
  TH,
  THead,
  TR,
  Table,
  TableShell,
} from "@/components/dashboard/data-table/table";
import { PageHeader } from "@/components/dashboard/page-header";
import { StatusPill } from "@/components/dashboard/status-pill";
import { PersonAvatar } from "@/components/employees/person-avatar";
import { CorrectionPanel } from "@/components/attendance/correction";
import { attendance as copy, filters, nav } from "@/content";
import { can, scopeToSupervisorId } from "@/constants/permissions";
import {
  readEnum,
  readNumber,
  readPage,
  readPageSize,
  readSearch,
  readSort,
  emptyPage,
} from "@/utils/query-params";
import {
  PUNCH_SORT_COLUMNS,
  PUNCH_SOURCES,
  PUNCH_STATUSES,
  PUNCH_TYPES,
  listPunchGuards,
  listPunches,
  listUnclosedPunches,
} from "@/api/attendance";
import { listZoneOptions } from "@/api/employees";
import { instantAt, isValidDateKey, toDateKey } from "@/utils/time";
import { useQuery } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/shared/useQueryParams";
import { useFmt } from "@/hooks/shared/useFmt";
import { useDocumentTitle } from "@/hooks/shared/useDocumentTitle";
import { useSession } from "@/context/UserContext";

const BASE_PATH = "/dashboard/attendance";

/**
 * The punch log — a surface the prototype did not have in any form.
 *
 * Its `GET /api/attendance/history` was `requireRole('guard')` and returned only
 * the caller's own rows, so on the product whose core record is attendance, no
 * admin could read a punch, no supervisor could check whether their team had
 * arrived, and nobody could correct anything.
 *
 * The unclosed-punch queue at the top is the other half. The prototype inferred
 * "on duty" from the last attendance row ever written, with no date bound, so a
 * guard who checked in three weeks ago and never checked out read as on duty
 * forever — while the performance calculation quietly dropped that punch from
 * duty hours. The state was simultaneously wrong on screen and missing from the
 * numbers; here it is a queue with a way to resolve it.
 */
export const Route = createFileRoute("/_main/dashboard/attendance")({
  component: () => (
    <RequirePermission action="attendance.read">
      <AttendancePage />
    </RequirePermission>
  ),
});

function AttendancePage() {
  useDocumentTitle(copy.title);
  const { user } = useSession();
  const params = useQueryParams();
  const fmt = useFmt();
  const scope = scopeToSupervisorId(user);
  const canCorrect = can(user.role, "attendance.correct");

  const sort = readSort(params, PUNCH_SORT_COLUMNS, "occurredAt");
  const rawFrom = typeof params.from === "string" ? params.from : undefined;
  const rawTo = typeof params.to === "string" ? params.to : undefined;
  const appliedFrom = rawFrom && isValidDateKey(rawFrom) ? rawFrom : undefined;
  const appliedTo = rawTo && isValidDateKey(rawTo) ? rawTo : undefined;

  const query = {
      search: readSearch(params),
      type: readEnum(params, "type", PUNCH_TYPES),
      status: readEnum(params, "status", PUNCH_STATUSES),
      source: readEnum(params, "source", PUNCH_SOURCES),
      guardId: readNumber(params, "guard"),
      zoneId: readNumber(params, "zone"),
      from: appliedFrom,
      to: appliedTo,
      scopeToSupervisorId: scope,
    sort: { column: sort.column, direction: sort.direction === "asc" ? ("asc" as const) : ("desc" as const) },
    page: readPage(params),
    pageSize: readPageSize(params),
  };

  const list = useQuery({
    queryKey: ["attendance", "list", query],
    queryFn: () => listPunches(query),
  });

  /* The exceptions queue is the whole scope, not the filtered page — it must not
     move when a filter does. Twelve hours is the API's threshold: a shift in
     progress is not an exception, and the longest template in the fixture is
     eight. */
  const unclosedQuery = useQuery({
    queryKey: ["attendance", "unclosed"],
    queryFn: listUnclosedPunches,
  });
  const guardsQuery = useQuery({
    queryKey: ["attendance", "guards"],
    queryFn: listPunchGuards,
    staleTime: 5 * 60_000,
  });
  const zonesQuery = useQuery({
    queryKey: ["employees", "zoneOptions"],
    queryFn: listZoneOptions,
    staleTime: 5 * 60_000,
  });

  const result = list.data ?? emptyPage<never>();
  const unclosed = unclosedQuery.data ?? [];
  const guards = guardsQuery.data ?? [];
  const zones = zonesQuery.data ?? [];

  const isFiltered = Boolean(
    readSearch(params) ||
      params.type ||
      params.status ||
      params.source ||
      params.guard ||
      params.zone ||
      appliedFrom ||
      appliedTo,
  );

  const columnCount = 7;

  return (
    <div className="mx-auto w-full max-w-shell px-6 py-8">
      <PageHeader
        eyebrow={nav.groups.operations}
        title={copy.title}
        lead={copy.lead}
        actions={
          canCorrect ? (
            <CorrectionPanel
              guards={guards.length > 0 ? guards : []}
              defaultAt={`${toDateKey(new Date(), user.timeZone)}T08:00`}
            />
          ) : null
        }
      />

      {!canCorrect ? <p className="mt-5 text-xs text-n6">{copy.readOnlyNote}</p> : null}

      {/* The exceptions queue, above the log. It is the only thing on this page
          that needs acting on, so it comes first — and it is absent entirely when
          there is nothing open, rather than showing a reassuring empty panel. */}
      {unclosed.length > 0 ? (
        <section className="mt-6 rounded-lg border border-orange/40 bg-orange/8 p-5">
          <h2 className="flex items-center gap-2 text-sm font-bold text-n9">
            <AlertTriangle aria-hidden className="size-4 text-orange-700" strokeWidth={1.8} />
            {copy.unclosedTitle}
          </h2>
          <p className="mt-1 text-xs text-n7">{copy.unclosedLead}</p>

          <ul className="mt-4 flex flex-col gap-px overflow-hidden rounded-lg bg-n4">
            {unclosed.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-center gap-3 bg-n0 px-4 py-3 text-sm"
              >
                <PersonAvatar name={entry.guardName} photoUrl={entry.photoUrl} />
                <Link
                  to={`/dashboard/employees/${entry.guardId}`}
                  className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                >
                  {entry.guardName}
                </Link>
                {entry.zoneName ? (
                  <span className="text-xs text-n6">{entry.zoneName}</span>
                ) : null}
                <span className="tabular text-xs text-n7">
                  {copy.unclosedSince} {fmt.dateTime(entry.occurredAt)}
                </span>
                <StatusPill tone="attention">
                  <span className="tabular">{fmt.count(Math.round(entry.hoursOpen))}</span>{" "}
                  {copy.hours}
                </StatusPill>

                {canCorrect ? (
                  <span className="ms-auto">
                    {/* Pre-filled with this guard and a check-out, so resolving
                        the exception is one panel rather than a form to re-derive. */}
                    <CorrectionPanel
                      guards={guards}
                      defaultGuardId={entry.guardId}
                      defaultType="check_out"
                      triggerLabel={copy.closeUnclosed}
                      variant="quiet"
                    />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="mt-6">
        <FilterBar
          params={params}
          basePath={BASE_PATH}
          searchPlaceholder={copy.searchPlaceholder}
          selects={[
            {
              key: "type",
              label: copy.filterType,
              value: typeof params.type === "string" ? params.type : "",
              options: [
                { value: "check_in", label: copy.checkIn },
                { value: "check_out", label: copy.checkOut },
              ],
            },
            {
              key: "status",
              label: copy.filterStatus,
              value: typeof params.status === "string" ? params.status : "",
              options: [
                { value: "ok", label: copy.insideZone },
                { value: "outside_zone", label: copy.outsideZone },
              ],
            },
            {
              key: "source",
              label: copy.filterSource,
              value: typeof params.source === "string" ? params.source : "",
              options: [
                { value: "mobile", label: copy.sourceMobile },
                { value: "manual", label: copy.sourceManual },
                { value: "kiosk", label: copy.sourceKiosk },
              ],
            },
            {
              key: "guard",
              label: copy.filterGuard,
              value: typeof params.guard === "string" ? params.guard : "",
              options: guards.map((guard) => ({ value: String(guard.id), label: guard.name })),
            },
            {
              key: "zone",
              label: copy.filterZone,
              value: typeof params.zone === "string" ? params.zone : "",
              options: zones.map((zone) => ({ value: String(zone.id), label: zone.name })),
            },
          ]}
        />
      </div>

      <form action={BASE_PATH} method="get" className="mt-4 flex flex-wrap items-end gap-3">
        {Object.entries(params).map(([key, value]) =>
          key === "from" || key === "to" || typeof value !== "string" ? null : (
            <input key={key} type="hidden" name={key} value={value} />
          ),
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterFrom}</span>
          <input
            type="date"
            name="from"
            defaultValue={rawFrom ?? ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-semibold text-n6">{copy.filterTo}</span>
          <input
            type="date"
            name="to"
            defaultValue={rawTo ?? ""}
            className="tabular h-10 rounded-lg border border-n4 bg-n0 px-3 text-sm text-n8 outline-none transition-colors focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        </label>
        <button
          type="submit"
          className="h-10 rounded-lg border border-n4 bg-n0 px-4 text-sm font-semibold text-n8 transition-colors duration-200 ease-brand hover:bg-n2"
        >
          {filters.apply}
        </button>

        {/* A native date input renders in the browser's locale, which can be
            mm/dd/yyyy inside an Arabic console. The applied range is echoed in the
            console's own format so what is filtered is never in doubt. */}
        {appliedFrom || appliedTo ? (
          <p className="tabular w-full text-[11px] font-semibold text-n7">
            {copy.dateRangeApplied}{" "}
            {appliedFrom ? fmt.dateNumeric(instantAt(appliedFrom, "12:00")) : "…"} –{" "}
            {appliedTo ? fmt.dateNumeric(instantAt(appliedTo, "12:00")) : "…"}
          </p>
        ) : null}
      </form>

      <TableShell className="mt-5">
        <Table>
          <THead>
            <SortHeader
              column="guard"
              label={copy.columnGuard}
              params={params}
              basePath={BASE_PATH}
              current={sort}
            />
            <TH>{copy.columnType}</TH>
            <SortHeader
              column="occurredAt"
              label={copy.columnTime}
              params={params}
              basePath={BASE_PATH}
              current={sort}
            />
            <SortHeader
              column="zone"
              label={copy.columnZone}
              params={params}
              basePath={BASE_PATH}
              current={sort}
            />
            <TH>{copy.columnStatus}</TH>
            <TH numeric>{copy.columnLate}</TH>
            <TH>{copy.columnSource}</TH>
          </THead>

          <TBody>
            {result.rows.length === 0 ? (
              <TEmpty colSpan={columnCount}>
                <EmptyState
                  variant={isFiltered ? "filtered" : "empty"}
                  title={isFiltered ? copy.emptyFilteredTitle : copy.emptyTitle}
                  lead={isFiltered ? copy.emptyFilteredLead : copy.emptyLead}
                  actionHref={isFiltered ? BASE_PATH : undefined}
                  actionLabel={isFiltered ? copy.clearFilters : undefined}
                />
              </TEmpty>
            ) : (
              result.rows.map((row) => (
                <TR key={row.id}>
                  <TD>
                    <div className="flex items-center gap-2.5">
                      <PersonAvatar name={row.guardName} photoUrl={row.photoUrl} />
                      <Link
                        to={`/dashboard/employees/${row.guardId}`}
                        className="font-semibold text-n9 transition-colors duration-200 ease-brand hover:text-orange-700"
                      >
                        {row.guardName}
                      </Link>
                    </div>
                  </TD>

                  <TD>
                    <span className="text-xs font-semibold text-n7">
                      {row.type === "check_in" ? copy.checkIn : copy.checkOut}
                    </span>
                  </TD>

                  <TD numeric>
                    <span className="tabular text-xs">{fmt.dateTime(row.occurredAt)}</span>
                  </TD>

                  <TD>
                    {row.zoneName ? (
                      <span className="text-xs text-n7">{row.zoneName}</span>
                    ) : (
                      <span className="text-xs text-n6">
                        {row.lat === null ? copy.noPosition : copy.noZone}
                      </span>
                    )}
                    {row.accuracyMeters !== null ? (
                      <span className="tabular mt-0.5 block text-[10px] text-n6">
                        {copy.columnAccuracy}: {fmt.distance(row.accuracyMeters)}
                      </span>
                    ) : null}
                  </TD>

                  <TD>
                    {/* Three outcomes, not two. A punch with no coordinates — a
                        manual correction, or a handset with no fix — cannot be
                        said to be inside anything, and claiming it is would put a
                        reassuring falsehood in the column that exists to be
                        trusted. `outside_zone` is the value the prototype's own
                        schema allowed and its controller never wrote, because it
                        rejected every out-of-zone punch outright. */}
                    {row.lat === null ? (
                      <StatusPill tone="muted">{copy.noPosition}</StatusPill>
                    ) : row.status === "outside_zone" ? (
                      <StatusPill tone="attention">{copy.outsideZone}</StatusPill>
                    ) : (
                      <StatusPill tone="positive">{copy.insideZone}</StatusPill>
                    )}
                  </TD>

                  <TD numeric>
                    {row.type === "check_out" ? (
                      <span className="text-xs text-n5">—</span>
                    ) : row.minutesLate === null ? (
                      <span className="text-[11px] text-n6">{copy.unscheduled}</span>
                    ) : row.minutesLate === 0 ? (
                      <span className="text-xs text-n6">{copy.onTime}</span>
                    ) : (
                      <span className="tabular text-xs font-bold text-orange-700">
                        {fmt.duration(row.minutesLate)}
                      </span>
                    )}
                  </TD>

                  <TD>
                    <span className="text-xs text-n7">
                      {row.source === "mobile"
                        ? copy.sourceMobile
                        : row.source === "manual"
                          ? copy.sourceManual
                          : copy.sourceKiosk}
                    </span>
                    {row.correctedByName ? (
                      <span className="mt-0.5 block text-[10px] text-n6">
                        {copy.correctedBy} {row.correctedByName}
                      </span>
                    ) : null}
                    {row.correctionReason ? (
                      <span className="mt-0.5 block max-w-[24ch] truncate text-[10px] text-n6">
                        {row.correctionReason}
                      </span>
                    ) : null}
                  </TD>
                </TR>
              ))
            )}
          </TBody>
        </Table>

        {result.rows.length > 0 ? (
          <Pagination
            page={result.page}
            pageCount={result.pageCount}
            pageSize={result.pageSize}
            total={result.total}
            params={params}
            basePath={BASE_PATH}
            numerals={user.numeralSystem}
          />
        ) : null}
      </TableShell>
    </div>
  );
}
